<template>
  <!-- RQ Complete Installation — laid out like the Laravel Pro panel (pro/rq/status-edit.vue "Return Items"):
       Notes · Breaker (Yes / No + Amperage / Brand) · Installation Items (picked from the catalogue, SKU, price,
       quantity, material cost). Texts / rules from Android CompleteInstallationFragment; POST orders/installation/complete -->
  <div id="rqCompleteModal" ref="modal" class="modal fade" tabindex="-1" aria-hidden="true">
    <div
      class="modal-dialog modal-dialog-centered mobile:!w-full mobile:!right-0 mobile:!px-3 tablet:px-0 tablet:!w-[520px] !absolute tablet:!right-4 !top-0 !m-0 h-full">
      <div class="modal-content">
        <div class="modal-body !p-3 !pb-0 font-roboto flex flex-col">
          <FxModalHeader :title="view === 'picker' ? 'Installation Items' : 'Complete Installation'" />

          <!-- form -->
          <div v-if="view === 'form'" class="flex-1 flex flex-col gap-[15px] px-[6px] pt-[12px] pb-[15px]">
            <p class="m-0 text-[16px] font-medium text-[#333333]">Return Items</p>

            <section class="rqSection">
              <h4 class="rqSectionTitle">Notes</h4>
              <label class="uqcLabel" for="rqcNotes">Do you want to return any items to office? <span
                  class="text-[#EE5851]">*</span></label>
              <textarea id="rqcNotes" v-model="notes" rows="4" maxlength="255" placeholder="Type your message here"
                class="w-full rounded-[8px] border border-[#E1E5EF] px-[12px] py-[8px] text-[14px] text-[#333333] outline-none resize-none focus:border-[#A084E8]"
                :class="{ '!border-[#EE5851]': errors.notes }" @input="errors.notes = ''" />
              <p v-if="errors.notes" class="m-0 text-[12px] text-[#EE5851]">{{ errors.notes }}</p>
            </section>

            <section class="rqSection">
              <h4 class="rqSectionTitle">Breaker</h4>
              <div class="flex flex-wrap items-center gap-x-[24px] gap-y-[8px]">
                <span class="text-[14px] text-[#666666]">Breaker Removed?</span>
                <label v-for="opt in YES_NO" :key="'b' + opt.value" class="rqRadio">
                  <input v-model="breaker" type="radio" :value="opt.value" class="hidden">
                  <span class="rqRadioDot" :class="{ on: breaker === opt.value }" />{{ opt.label }}
                </label>
              </div>
              <div v-if="breaker" class="grid grid-cols-2 gap-[15px]">
                <div>
                  <div class="uqcLabel">Amperage <span class="text-[#EE5851]">*</span></div>
                  <inputComponent ref="amperage" v-model="amperage" name="rqcAmperage" placeholder="e.g. 30" required
                    required-message="Amperage field required" :regex="NUM" error-message="Please enter a valid number"
                    inputmode="decimal" :custom-class="fieldClass" />
                </div>
                <div>
                  <div class="uqcLabel">Brand <span class="text-[#EE5851]">*</span></div>
                  <inputComponent ref="brand" v-model="brand" name="rqcBrand" placeholder="e.g. Square D" required
                    required-message="Brand field required" :custom-class="fieldClass" />
                </div>
              </div>
            </section>

            <section class="rqSection">
              <div class="flex items-center justify-between gap-[10px] pb-[12px] border-b border-[#EFEFEF]">
                <h4 class="m-0 text-[15px] font-medium text-[#8479E1]">Installation Items</h4>
                <button type="button"
                  class="h-[30px] px-[14px] rounded-full border-1 border-[#00B7C2] bg-[#fff] text-[13px] text-[#00B7C2] whitespace-nowrap hover:bg-[#00B7C2] hover:text-[#fff] transition"
                  @click="openPicker">+ Add Installation Items</button>
              </div>
              <p v-if="!rows.length" class="m-0 text-[14px] text-[#999999]">No installation items added.</p>
              <div v-for="(row, index) in rows" :key="row.id" class="flex items-start gap-[12px]">
                <div class="flex-1 min-w-0">
                  <div class="uqcLabel">Item Name</div>
                  <div class="px-[12px] py-[9px] rounded-[8px] border border-[#E1E5EF] bg-[#F7F8FA] text-[14px] text-[#333333] break-words">
                    {{ row.name }}</div>
                  <span class="text-[12px] text-[#666666]">SKU: {{ row.sku || '-' }} · {{ money(row.price) }} per unit</span>
                </div>
                <div class="w-[90px] flex-shrink-0">
                  <div class="uqcLabel">Quantity <span class="text-[#EE5851]">*</span></div>
                  <input v-model.number="row.quantity" type="number" min="1" max="9999" step="1"
                    class="w-full h-[40px] px-[10px] rounded-[8px] border border-[#E1E5EF] text-[14px] text-[#333333] outline-none focus:border-[#A084E8]"
                    :class="{ '!border-[#EE5851]': errors.items && !(row.quantity >= 1) }"
                    @keydown="['e', 'E', '+', '-', '.'].includes($event.key) && $event.preventDefault()">
                </div>
                <button type="button" :aria-label="'Remove ' + row.name"
                  class="mt-[22px] w-[40px] h-[40px] flex-shrink-0 rounded-full border-1 border-[#F67280] bg-[#fff] text-[#F67280] flex items-center justify-center hover:bg-[#F67280] hover:text-[#fff] transition"
                  @click="rows.splice(index, 1)">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M4 6.5h16M9.5 6.5V4h5v2.5M6.5 6.5l.9 13.5h9.2l.9-13.5M10.3 10v6.5M13.7 10v6.5"
                      stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </button>
              </div>
              <p v-if="errors.items" class="m-0 text-[12px] text-[#EE5851]">{{ errors.items }}</p>
              <div v-if="rows.length" class="flex justify-end text-[14px] text-[#333333]">
                Material cost:&nbsp;<span class="font-bold">{{ money(materialCost) }}</span>
              </div>
            </section>
          </div>

          <!-- catalogue picker (Android SelectInstallationItemDialog / Pro panel modal): search + multi-select -->
          <div v-else class="flex-1 flex flex-col gap-[12px] px-[6px] pt-[12px] pb-[15px]">
            <div>
              <div class="uqcLabel">Search</div>
              <input v-model="search" type="text" placeholder="Search by name or SKU"
                class="w-full h-[40px] px-[12px] rounded-[8px] border border-[#E1E5EF] text-[14px] text-[#333333] outline-none focus:border-[#A084E8]"
                @input="onSearch">
            </div>
            <div>
              <label v-for="item in catalogue" :key="'c' + item.id"
                class="m-0 flex items-center gap-[10px] px-[5px] py-[10px] border-b border-[#EFEFEF] cursor-pointer">
                <input type="checkbox" class="w-[18px] h-[18px] flex-shrink-0 accent-[#8479E1]" :checked="!!picked[item.id]"
                  @change="togglePick(item)">
                <span class="flex-1 min-w-0 flex flex-col">
                  <span class="text-[14px] text-[#333333] break-words">{{ item.name }}</span>
                  <span class="text-[12px] text-[#666666]">SKU: {{ item.sku || '-' }}</span>
                </span>
                <span class="text-[14px] text-[#333333] whitespace-nowrap">{{ money(item.price) }}</span>
              </label>
              <p v-if="loadingCatalogue" class="m-0 py-[15px] text-center text-[13px] text-[#999999]">Loading…</p>
              <p v-else-if="!catalogue.length" class="m-0 py-[15px] text-center text-[13px] text-[#999999]">No items found</p>
              <div v-else-if="page < totalPages" class="flex justify-center pt-[12px]">
                <button type="button" class="fxBtnSecondary" @click="loadCatalogue(page + 1)">Load more</button>
              </div>
            </div>
          </div>

          <div class="uqcFooter flex items-center gap-[10px]">
            <button v-if="view === 'picker'" type="button" class="uqcBtnBack" aria-label="Back" @click="view = 'form'">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M19 12H5m6-6-6 6 6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round" />
              </svg>
            </button>
            <div class="flex-1 flex justify-end gap-[10px]">
              <template v-if="view === 'picker'">
                <button type="button" class="uqcBtnCancel" @click="view = 'form'">Cancel</button>
                <button type="button" class="uqcBtnNext" @click="addPicked">Add</button>
              </template>
              <template v-else>
                <button type="button" class="uqcBtnCancel" data-bs-dismiss="modal">Cancel</button>
                <button type="button" class="uqcBtnNext" :disabled="busy" @click="confirmSubmit">Complete Installation</button>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Modal } from "bootstrap";
import { useToast } from "vue-toastification";
import FxModalHeader from "../nest_components/FxModalHeader.vue";
import inputComponent from "../partialComponent/inputComponent.vue";
import { rqApi } from "@/services/rqApi";
import { disableScroll, enableScroll } from "../../helpers/scrollHelper";

const toast = useToast();
const PAGE_SIZE = 20;

export default {
  components: { FxModalHeader, inputComponent },
  props: {
    orderId: { type: [Number, String], required: true },
  },
  emits: ["completed"],
  data() {
    return {
      YES_NO: [{ label: "Yes", value: true }, { label: "No", value: false }],
      NUM: /^\d+(\.\d+)?$/,
      fieldClass: "!h-[40px] !px-[12px] !rounded-[8px] !border-[#E1E5EF] !text-[14px] !text-[#333333]",
      view: "form",
      busy: false,
      notes: "",
      breaker: true, // Android and the Pro panel start on "Yes"
      amperage: "",
      brand: "",
      rows: [], // { id, name, sku, price, quantity }
      errors: {},
      // picker
      search: "",
      catalogue: [],
      picked: {},
      page: 1,
      totalPages: 1,
      loadingCatalogue: false,
    };
  },
  computed: {
    materialCost() {
      return this.rows.reduce((t, r) => t + Number(r.price || 0) * Number(r.quantity || 0), 0);
    },
  },
  mounted() {
    this.bsModal = new Modal(this.$refs.modal, { backdrop: "static", keyboard: false });
    this.$refs.modal.addEventListener("hidden.bs.modal", enableScroll);
  },
  beforeUnmount() {
    this.$refs.modal?.removeEventListener("hidden.bs.modal", enableScroll);
    clearTimeout(this.searchTimer);
    this.bsModal?.dispose();
  },
  methods: {
    open() {
      Object.assign(this, { view: "form", notes: "", breaker: true, amperage: "", brand: "", rows: [], errors: {} });
      disableScroll();
      this.bsModal.show();
    },
    money(value) {
      return "$" + Number(value || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    // picker
    openPicker() {
      this.picked = Object.fromEntries(this.rows.map(r => [r.id, r]));
      this.search = "";
      this.view = "picker";
      this.loadCatalogue(1);
    },
    onSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.loadCatalogue(1), 700);
    },
    async loadCatalogue(page) {
      this.loadingCatalogue = true;
      const search = this.search.trim();
      const res = await rqApi.installationItems({ order_id: this.orderId, limit: PAGE_SIZE, page, ...(search ? { search } : {}) });
      this.loadingCatalogue = false;
      if (!res.ok) return toast.error(res.message || "Unable to load installation items.");
      const list = Array.isArray(res.body) ? res.body : [];
      this.catalogue = page === 1 ? list : this.catalogue.concat(list);
      this.page = page;
      this.totalPages = Number(res.paging?.total_pages) || page;
    },
    togglePick(item) {
      if (this.picked[item.id]) delete this.picked[item.id];
      else this.picked[item.id] = item;
    },
    // keeps the quantity of rows already in the list, new ones start at 1
    addPicked() {
      this.rows = Object.values(this.picked).map(item => {
        const row = this.rows.find(r => r.id === item.id);
        return row || { id: item.id, name: item.name, sku: item.sku, price: item.price, quantity: 1 };
      });
      this.errors.items = "";
      this.view = "form";
    },

    // submit
    validate() {
      const errors = {};
      if (!this.notes.trim()) errors.notes = "Notes field required";
      const bad = this.rows.find(r => !(Number.isInteger(r.quantity) && r.quantity >= 1));
      if (bad) errors.items = `Please enter quantity for ${bad.name}`;
      this.errors = errors;
      const fieldsOk = this.breaker ? [this.$refs.amperage, this.$refs.brand].map(f => !f.validate()).every(Boolean) : true;
      return fieldsOk && !Object.keys(errors).length;
    },
    confirmSubmit() {
      if (!this.validate()) return;
      this.$dialog.show({
        title: "Complete Installation",
        text: "Are you sure you have completed new unit installation?",
        buttonText: "Yes",
        onConfirm: () => this.submit(),
      });
    },
    async submit() {
      const payload = {
        order_id: Number(this.orderId),
        breaker_removed: this.breaker ? "1" : "0",
        notes: this.notes.trim(),
        ...(this.breaker ? { amperage: String(this.amperage).trim(), brand: String(this.brand).trim() } : {}),
        ...(this.rows.length ? { installation_items: this.rows.map(r => ({ id: r.id, quantity: r.quantity })) } : {}),
      };
      this.busy = true;
      const res = await rqApi.installationComplete(payload);
      this.busy = false;
      this.$emit("completed", res);
      if (res.ok) this.bsModal.hide();
    },
  },
};
</script>

<style scoped>
.rqSection {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 15px;
  border-radius: 10px;
  box-shadow: 0px 0px 10px 0px #00000026;
}

.rqSectionTitle {
  margin: 0;
  padding-bottom: 12px;
  border-bottom: 1px solid #EFEFEF;
  font-size: 15px;
  font-weight: 500;
  color: #8479E1;
}

.rqRadio {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: 14px;
  color: #666666;
  cursor: pointer;
}

.rqRadioDot {
  width: 20px;
  height: 20px;
  border-radius: 999px;
  border: 1px solid #CCCCCC;
  display: flex;
  align-items: center;
  justify-content: center;
}

.rqRadioDot.on {
  border-color: #8479E1;
}

.rqRadioDot.on::after {
  content: "";
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: #8479E1;
}
</style>
